// The points maths and the evidence line behind a score's hover (GRO-411). Plain .ts so node --test
// can load it.
//
// A score is Σ(category score × weight) ÷ 100, and when the weights do not sum to 100 and the engine
// scales them, the sum is then multiplied by 100 ÷ (weight sum). The hover shows every step, so a
// reader can add the lines up and land on the number in the cell. Which of the two the engine used
// is not stored, so it is read off the stored total: whichever lands nearer wins.

import type { CompetitorFlagDetail, ScoreCategoryDetail } from '../api/types.ts';
import { categoryLabel, formatScore } from './scores.ts';

export type LineState = 'scored' | 'default' | 'disabled';

export interface BreakdownLine {
  category: string;
  label: string;
  state: LineState;
  score: number | null;
  weight: number | null;
  // score × weight ÷ 100; null for a disabled category or one with no weight.
  points: number | null;
  evidence: string | null;
  // A disabled category's reason from the settings ("LinkedIn not yet measured").
  disabledReason: string | null;
}

export interface Breakdown {
  lines: BreakdownLine[];
  // Σ of the enabled lines' points, before any scaling.
  pointsSum: number;
  // The same sum of the lines as drawn (each to one decimal), so the column visibly adds up.
  shownSum: number;
  shownScaledSum: number;
  weightSum: number;
  // True when the weights do not sum to 100 and the engine divided by their sum.
  scaled: boolean;
  // pointsSum × 100 ÷ weightSum: what the total would be with the scale step.
  scaledSum: number;
  // The total the lines add up to, after the scale step when there is one.
  computedTotal: number;
  storedTotal: number | null;
  // Whether computedTotal and the stored total agree to within rounding.
  agrees: boolean;
  // False when a counted category has no weight, so no sum can be shown.
  weighted: boolean;
}

// The stored total is rounded to one decimal, so two numbers that differ by less than this agree.
const AGREE_WITHIN = 0.15;
const WEIGHT_TOTAL = 100;
const SHOWN_NAMES = 5;
const SHOWN_ITEMS = 3;

export const NOT_MEASURED = 'not yet measured';

// Switched off: the engine marks it `excluded` (score null, weight 0) and the router sends `enabled: false`.
// A weight of 0 with `used_default` is not switched off: it is an input with no data that the engine left
// out of the score (GRO-429), shown as "data missing, left out" rather than as disabled.
export function isLeftOut(category: { used_default?: boolean; weight?: number | null }): boolean {
  return category.weight === 0 && category.used_default === true;
}

export function isDisabled(category: { enabled?: boolean; excluded?: boolean; weight?: number | null; used_default?: boolean }): boolean {
  if (category.enabled === false || category.excluded === true) return true;
  return category.weight === 0 && !isLeftOut(category);
}

function lineLabel(category: string): string {
  return categoryLabel(category);
}

export function lineState(category: ScoreCategoryDetail): LineState {
  if (isDisabled(category)) return 'disabled';
  return category.used_default ? 'default' : 'scored';
}

export function categoryPoints(score: number | null, weight: number | null): number | null {
  if (score == null || weight == null) return null;
  return (score * weight) / WEIGHT_TOTAL;
}

export function buildBreakdown(categories: ScoreCategoryDetail[], storedTotal: number | null): Breakdown {
  const lines: BreakdownLine[] = categories.map(category => {
    const state = lineState(category);
    const counted = state !== 'disabled';
    return {
      category: category.category,
      label: lineLabel(category.category),
      state,
      score: counted ? category.score : null,
      weight: counted ? category.weight : null,
      points: counted ? categoryPoints(category.score, category.weight) : null,
      evidence: counted ? evidenceLine(category) : null,
      disabledReason: counted ? null : category.excluded_reason ?? category.reasons?.[0] ?? null,
    };
  });

  // Only counted categories add up; a counted one with no weight means the sum cannot be drawn.
  const counted = lines.filter(line => line.state !== 'disabled');
  const weighted = counted.length > 0 && counted.every(line => line.points != null);
  const pointsSum = counted.reduce((sum, line) => sum + (line.points ?? 0), 0);
  const shownSum = counted.reduce((sum, line) => sum + roundedPoints(line.points), 0);
  const weightSum = counted.reduce((sum, line) => sum + (line.weight ?? 0), 0);
  const scaledSum = weightSum > 0 ? (pointsSum * WEIGHT_TOTAL) / weightSum : 0;

  // Scaled when the weights are off 100 and scaling lands nearer the stored total (or no total to ask).
  const offHundred = Math.abs(weightSum - WEIGHT_TOTAL) > 0.001;
  const scaled = offHundred && (storedTotal == null
    || Math.abs(scaledSum - storedTotal) <= Math.abs(pointsSum - storedTotal));
  const computedTotal = scaled ? scaledSum : pointsSum;
  const agrees = weighted && storedTotal != null && Math.abs(computedTotal - storedTotal) <= AGREE_WITHIN;
  const shownScaledSum = weightSum > 0 ? (shownSum * WEIGHT_TOTAL) / weightSum : 0;
  return { lines, pointsSum, shownSum, shownScaledSum, weightSum, scaled, scaledSum, computedTotal, storedTotal, agrees, weighted };
}

// ---------------------------------------------------------------------------------------------
// Engagement as Self-serve plus boosts (GRO-431 change 1): the Self-serve score counts in full and
// each boost adds score ÷ 100 × its most points, capped at 100. Each line's weight is its most points,
// so `categoryPoints` (score × weight ÷ 100) is already the line's points.

export const SELF_SERVE_PLUS_BOOSTS = 'self_serve_plus_boosts';
export const SELF_SERVE = 'playground';
const ENGAGEMENT_CAP = 100;

export interface BoostBreakdown {
  // The Self-serve line, or null when the payload has none.
  base: BreakdownLine | null;
  boosts: BreakdownLine[];
  // "Self-serve 73 + boosts (Web intent +6, News +0)".
  summary: string;
  // Σ of the counted lines' points, before the cap.
  uncappedTotal: number;
  computedTotal: number;
  capped: boolean;
  storedTotal: number | null;
  agrees: boolean;
}

// A line adds points only when it is scored or fell to a default that still counts (weight above 0).
function addsPoints(line: BreakdownLine): boolean {
  return line.state !== 'disabled' && line.points != null && (line.weight ?? 0) > 0;
}

// "+6" for a boost's points, rounded as the board rounds scores.
export function boostPointsText(points: number | null): string {
  return `+${formatScore(points ?? 0)}`;
}

export function buildBoostBreakdown(categories: ScoreCategoryDetail[], storedTotal: number | null): BoostBreakdown {
  const { lines } = buildBreakdown(categories, storedTotal);
  const base = lines.find(line => line.category === SELF_SERVE) ?? null;
  const boosts = lines.filter(line => line.category !== SELF_SERVE);

  // The total: every line that adds points, then the cap.
  const uncappedTotal = lines.filter(addsPoints).reduce((sum, line) => sum + (line.points ?? 0), 0);
  const capped = uncappedTotal > ENGAGEMENT_CAP;
  const computedTotal = Math.min(ENGAGEMENT_CAP, uncappedTotal);
  const agrees = storedTotal != null && Math.abs(computedTotal - storedTotal) <= AGREE_WITHIN;

  // The one-line working: Self-serve's score, then each boost that has data (a real 0 shows as +0).
  const baseText = base && addsPoints(base) ? `Self-serve ${formatScore(base.score)}` : 'Self-serve no data';
  const boostTexts = boosts.filter(addsPoints).map(line => `${line.label} ${boostPointsText(line.points)}`);
  const summary = boostTexts.length ? `${baseText} + boosts (${boostTexts.join(', ')})` : `${baseText}, no boosts`;
  return { base, boosts, summary, uncappedTotal, computedTotal, capped, storedTotal, agrees };
}

function roundedPoints(value: number | null): number {
  return value == null ? 0 : Math.round(value * 10) / 10;
}

// One decimal for points: "25.9", and "0.0" rather than "0".
export function formatPoints(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return roundedPoints(value).toFixed(1);
}

// ---------------------------------------------------------------------------------------------
// The evidence line: one line per category from the stored breakdown, read as generically as it can.

const PART_LABELS: Record<string, string> = {
  level: 'Level', growth: 'Growth', users: 'Users', signups: 'Sign-ups', sign_ups: 'Sign-ups',
  technical_share: 'Technical share', ai_depth: 'AI depth', momentum: 'Momentum',
};
const PLAYGROUND_PARTS = ['level', 'growth', 'users', 'signups', 'sign_ups'];
const AI_TEAM_PARTS = ['technical_share', 'ai_depth', 'momentum'];
const NAME_KEYS = ['account', 'name', 'title', 'path', 'page', 'provider', 'text', 'headline'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// One value as text: a string or number as is, an object by its first naming key, else null.
function textOf(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (isRecord(value)) {
    for (const key of NAME_KEYS) {
      const named = value[key];
      if (typeof named === 'string' && named.trim()) return named.trim();
    }
  }
  return null;
}

function listOf(value: unknown): string[] {
  const items = Array.isArray(value) ? value : value == null ? [] : [value];
  return items.map(textOf).filter((item): item is string => item != null);
}

function firstText(details: Record<string, unknown> | undefined, keys: string[]): string | null {
  for (const key of keys) {
    const text = textOf(details?.[key]);
    if (text) return text;
  }
  return null;
}

function firstList(details: Record<string, unknown> | undefined, keys: string[]): string[] {
  for (const key of keys) {
    const items = listOf(details?.[key]);
    if (items.length) return items;
  }
  return [];
}

function withMore(items: string[], shown: number): string {
  const extra = items.length - shown;
  return items.slice(0, shown).join(', ') + (extra > 0 ? ` +${extra} more` : '');
}

function hostOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

// A part's number: a share held as 0-1 reads as a percent; anything else as a rounded number.
function partText(key: string, value: number): string {
  const label = PART_LABELS[key] ?? categoryLabel(key);
  const shown = key.endsWith('share') && value <= 1 ? `${Math.round(value * 100)}%` : formatScore(value);
  return `${label} ${shown}`;
}

function numberOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

// The named parts that are present, from `parts` then `details`, as "Level 70 · Growth 50".
function partsLine(category: ScoreCategoryDetail, keys: string[]): string | null {
  const seen = new Set<string>();
  const texts: string[] = [];
  for (const key of keys) {
    const value = numberOf(category.parts?.[key]) ?? numberOf(category.details?.[key]);
    const label = PART_LABELS[key] ?? key;
    if (value == null || seen.has(label)) continue;
    seen.add(label);
    texts.push(partText(key, value));
  }
  return texts.length ? texts.join(' · ') : null;
}

function quoteLine(category: ScoreCategoryDetail): string | null {
  const first = category.evidence[0];
  const quote = firstText(category.details, ['top_quote', 'quote', 'excerpt']) ?? first?.text ?? null;
  if (!quote) return null;
  const source = firstText(category.details, ['source', 'source_name', 'publisher']) ?? hostOf(first?.url);
  const day = firstText(category.details, ['day', 'date']) ?? first?.day ?? null;
  const origin = [source, day].filter(Boolean).join(', ');
  return `“${quote}”${origin ? ' — ' + origin : ''}`;
}

// An evidence line "CallHippo: similarity 0.61" as "CallHippo 0.61", so the hover shows why the number is what it is.
function neighbourText(evidenceText: string): string {
  const marker = ': similarity ';
  const at = evidenceText.lastIndexOf(marker);
  if (at < 0) return evidenceText.split(':')[0]!.trim();
  return `${evidenceText.slice(0, at).trim()} ${evidenceText.slice(at + marker.length).trim()}`;
}

function lookalikeLine(category: ScoreCategoryDetail): string | null {
  const named = firstList(category.details, ['nearest_customers', 'customers']);
  const fromEvidence = category.evidence.map(entry => neighbourText(entry.text)).filter(Boolean);
  const names = (named.length ? named : fromEvidence).slice(0, SHOWN_NAMES);
  return names.length ? `Nearest customers: ${names.join(', ')}` : null;
}

function hiringLine(category: ScoreCategoryDetail): string | null {
  const roles = firstList(category.details, ['roles', 'titles']);
  const fromEvidence = category.evidence.map(entry => entry.text).filter(Boolean);
  const all = roles.length ? roles : fromEvidence;
  return all.length ? `Roles: ${withMore(all, SHOWN_ITEMS)}` : null;
}

function competitorLine(category: ScoreCategoryDetail): string | null {
  const provider = firstText(category.details, ['provider', 'providers']);
  if (provider) return `Provider: ${provider}`;
  return category.reasons[0] ?? category.evidence[0]?.text ?? null;
}

// A web-intent evidence text starts with the page path ("/pricing first seen, ...").
function pagesLine(category: ScoreCategoryDetail): string | null {
  const pages = firstList(category.details, ['pages']);
  const fromEvidence = category.evidence
    .map(entry => entry.text.split(/\s+/)[0] ?? '')
    .filter(token => token.startsWith('/'));
  const all = pages.length ? pages : fromEvidence;
  return all.length ? `Pages: ${withMore(all, SHOWN_ITEMS + 1)}` : null;
}

// The first (best) news item with its day and its link: the link is written out in full so the hover's
// LinkedText draws it as a clickable source. Further items are counted, not listed.
function newsLine(category: ScoreCategoryDetail): string | null {
  const first = category.evidence[0];
  const item = firstText(category.details, ['item', 'headline', 'title']) ?? first?.text ?? null;
  if (!item) return null;
  const extra = category.evidence.length - 1;
  const origin = first?.day ? ` (${first.day})` : '';
  const link = first?.url ? ` ${first.url}` : ' (no link on file)';
  const more = extra > 0 ? ` +${extra} more` : '';
  return `${item}${origin}${link}${more}`;
}

// Whatever scalar fields `details` holds, for a category this file has no rule for.
function detailsLine(details: Record<string, unknown> | undefined): string | null {
  if (!details) return null;
  const texts: string[] = [];
  for (const [key, value] of Object.entries(details)) {
    const text = Array.isArray(value) ? withMore(listOf(value), SHOWN_ITEMS) : textOf(value);
    if (text) texts.push(`${categoryLabel(key)}: ${text}`);
    if (texts.length >= SHOWN_ITEMS) break;
  }
  return texts.length ? texts.join(' · ') : null;
}

// The voice categories that quote the evidence pack: the two lines since 26 Oct (GRO-465), and the one
// `voice_product` line that scores stored before then still hold.
const VOICE_QUOTE_CATEGORIES: ReadonlySet<string> = new Set(['voice_is_product', 'voice_use_case', 'voice_product']);

function byCategory(category: ScoreCategoryDetail): string | null {
  const name = category.category;
  if (VOICE_QUOTE_CATEGORIES.has(name) || name.startsWith('tech')) return quoteLine(category);
  if (name === 'lookalikes') return lookalikeLine(category);
  if (name === 'ai_team') return partsLine(category, AI_TEAM_PARTS);
  if (name === 'hiring') return hiringLine(category);
  if (name === 'competitor') return competitorLine(category);
  if (name === 'web_intent') return pagesLine(category);
  if (name === 'playground') return partsLine(category, PLAYGROUND_PARTS);
  if (name === 'news' || name === 'voice_news') return newsLine(category);
  return null;
}

// The category's evidence as one line; null when the breakdown holds nothing to show. A category
// with no rule, or whose rule found nothing, falls back to its details, then its first evidence,
// then its first reason, so a new scorer's fields show up before this file knows its name.
export function evidenceLine(stored: ScoreCategoryDetail): string | null {
  // An older or partial payload may lack the lists; read them as empty.
  const category = { ...stored, evidence: stored.evidence ?? [], reasons: stored.reasons ?? [] };
  return byCategory(category)
    ?? detailsLine(category.details)
    ?? category.evidence[0]?.text
    ?? category.reasons[0]
    ?? null;
}

// ---------------------------------------------------------------------------------------------
// The competitor flag (GRO-425): the one hard override, drawn as its own "Competitor" line under
// the sum. Flagged: the categories add up to the uncapped score and the line holds it to the cap.
// Lifted by a "Not a competitor" override: the line says who lifted it, when and what it lifted.

export interface CompetitorFlagLine {
  // "capped at 39.9", or "flag lifted".
  calc: string;
  // The score after the line: the cap when it applied, else null (the line changes nothing).
  points: number | null;
  why: string;
  lifted: boolean;
}

// The day of an ISO timestamp, "2026-10-22"; the text itself when it is not one.
function dayOf(stamp: string | null): string | null {
  if (!stamp) return null;
  return stamp.slice(0, 10);
}

export function competitorFlagLine(flag: CompetitorFlagDetail | null | undefined): CompetitorFlagLine | null {
  if (!flag) return null;
  const reasons = (flag.reasons ?? []).join(' · ');
  if (flag.flagged) {
    const cap = flag.capped_at;
    const uncapped = flag.uncapped_fit_score;
    const capped = cap != null && uncapped != null && uncapped > cap;
    return {
      calc: cap != null ? `capped at ${formatScore(cap)}` : 'Fit held low',
      points: capped ? cap : null,
      why: (capped ? `Fit held to low from ${formatScore(uncapped)}: ` : 'Competitor: ') + (reasons || 'flagged'),
      lifted: false,
    };
  }
  if (flag.overridden) {
    const who = flag.override_by ?? 'someone';
    const day = dayOf(flag.override_at);
    const because = flag.override_reason ? ` (“${flag.override_reason}”)` : '';
    const lifted = reasons ? ` Lifted: ${reasons}` : '';
    return {
      calc: 'flag lifted',
      points: null,
      why: `Marked not a competitor by ${who}${day ? ' on ' + day : ''}${because}.${lifted}`,
      lifted: true,
    };
  }
  return null;
}

// The total the category lines should add up to: the uncapped Fit when the cap applied.
export function linesTotal(storedTotal: number | null, flag: CompetitorFlagDetail | null | undefined): number | null {
  if (flag?.flagged && flag.uncapped_fit_score != null) return flag.uncapped_fit_score;
  return storedTotal;
}
